import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { BreathingAura } from '../components/BreathingAura';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CheckIn = () => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleVoiceTranscript = (transcript) => {
    setText((prev) => prev + ' ' + transcript);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error('Please enter some text');
      return;
    }

    setLoading(true);
    try {
      const data = await api.createCheckIn(text);
      setResult(data);
      toast.success('Check-in recorded successfully!');
      setText('');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Maximum')) {
        toast.error('You have reached the maximum of 3 check-ins for today');
      } else {
        toast.error('Failed to record check-in');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (label) => {
    if (label?.includes('Positive')) return 'text-primary';
    if (label?.includes('Stressed')) return 'text-destructive';
    return 'text-accent';
  };

  return (
    <div className="min-h-screen relative">
      <BreathingAura variant={result ? 'focus' : 'calm'} />
      
      <div className="relative z-10">
        <div className="container mx-auto px-6 py-8 max-w-4xl">
          <Button
            data-testid="back-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="rounded-full mb-8"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="text-center mb-12">
              <h1 className="text-5xl md:text-6xl font-serif font-light text-foreground tracking-tight mb-4">
                Daily Check-in
              </h1>
              <p className="text-muted-foreground text-lg">How are you feeling right now?</p>
            </div>

            {!result ? (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8">
                  <div className="mb-8">
                    <VoiceRecorder onTranscript={handleVoiceTranscript} />
                  </div>

                  <div className="relative">
                    <Textarea
                      data-testid="checkin-textarea"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Describe your feelings, thoughts, or what's on your mind..."
                      className="min-h-[200px] bg-input/50 border-transparent focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-2xl text-lg placeholder:text-muted-foreground/50 resize-none"
                    />
                  </div>
                </div>

                <div className="flex justify-center">
                  <Button
                    data-testid="submit-checkin-button"
                    type="submit"
                    disabled={loading || !text.trim()}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-12 py-6 text-lg transition-all hover:scale-105 active:scale-95 shadow-[0_0_30px_-5px_rgba(141,163,153,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      'Submit Check-in'
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                <div className="bg-card/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8">
                  <h2 className="text-2xl font-serif font-light text-foreground mb-6">Your Analysis</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Sentiment</div>
                      <div className={`text-3xl font-light ${getSentimentColor(result.sentiment_label)}`}>
                        {result.sentiment_label} ({result.sentiment_score}/10)
                      </div>
                    </div>

                    {result.stress_triggers?.length > 0 && (
                      <div>
                        <div className="text-sm text-muted-foreground mb-3">Stress Triggers</div>
                        <div className="flex flex-wrap gap-2">
                          {result.stress_triggers.map((trigger, i) => (
                            <span
                              key={i}
                              className="px-4 py-2 bg-destructive/20 text-destructive rounded-full text-sm"
                            >
                              {trigger}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="text-sm text-muted-foreground mb-3">Coping Suggestions</div>
                      <ul className="space-y-3">
                        {result.coping_suggestions?.map((suggestion, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                            <span className="text-foreground leading-relaxed">{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Burnout Risk</div>
                      <div className={`text-xl font-light ${getSentimentColor(result.burnout_risk)}`}>
                        {result.burnout_risk}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 justify-center">
                  <Button
                    data-testid="new-checkin-button"
                    onClick={() => setResult(null)}
                    className="bg-secondary/20 text-secondary hover:bg-secondary/30 rounded-full px-8 py-4 border border-secondary/20"
                  >
                    New Check-in
                  </Button>
                  <Button
                    data-testid="view-dashboard-button"
                    onClick={() => navigate('/dashboard')}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4"
                  >
                    View Dashboard
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CheckIn;